/**
 * EL ASISTENTE PASO A PASO DEL ARMADOR (MAD-ASISTENTE-UN-PASO-T1, maqueta aprobada por Josse, Ares #43601). Sin DOM.
 *
 * Contrato de pantalla: la maqueta https://claude.ai/artifact/FTvBEHapj9gvLDEV5c6pc7 (version 1793763895-2c02). Una
 * pantalla a la vez: por cada seccion del cliente, 1 Tipo (si su linea principal tiene 3 o mas tipos), 2 Cantidades y
 * 3 Revision (si hay algo que solo se confirma); al final el Resumen. Las secciones marcadas "no aplica en esta obra"
 * se saltan con Atras y Siguiente (Ares #43603). Lo prueba scripts/check-asistente-pasos.mjs.
 *
 * @module application/presupuestos/asistentePasos
 */
import { precioDeEstandar } from './itemEstandar.js';

/** Secciones sin pantalla de Tipo aunque una linea tenga varios tipos (Ares #43601: Acabados). */
export const SIN_PANTALLA_DE_TIPO = new Set([9]);
const MIN_TIPOS = 3;
const GLOBAL = /^(gnral|global|gl|glb)$/i;

/**
 * Que pide una linea: 'dentro' (va dentro de otra partida, no se cobra aparte), 'chequeo' (no lleva cantidad: por
 * confirmar, sin item en la plantilla o cobro global) o 'cantidad'.
 */
export function claseDeLinea(l, est = null) {
  if (l?.estado === 'dentro_de_otro') return 'dentro';
  if (l?.estado === 'por_confirmar' || l?.estado === 'sin_item_en_plantilla') return 'chequeo';
  const u = String(est?.estandar?.[0]?.unidad || l?.items?.[0]?.unidad || '').trim();
  return GLOBAL.test(u) ? 'chequeo' : 'cantidad';
}

/**
 * Las tarjetas de tipo de una linea con item estandar: una por tipo, en el orden del estandar, con el item de la
 * plantilla de su primer estandar que la plantilla trae (itemDeRef busca por libro:hoja:celda en TODA la plantilla: el
 * estandar de una linea apunta a items que la lista del cliente no le pone), el precio que se muestra (el confirmado o
 * la referencia) y si esta por confirmar. Sin itemDeRef, solo los items de la linea (filasDeLinea del grupo 4).
 */
export function tarjetasDeLinea(l, est, itemDeRef = null) {
  if (!l || !est?.estandar?.length) return [];
  const buscar = itemDeRef || ((ref) => (l.items || []).find((it) => String(it.fuenteId) === ref) || null);
  const porTipo = new Map();
  for (const e of est.estandar) {
    if (!e.tipo || porTipo.has(e.tipo)) continue;
    const item = (e.items || []).map((i) => buscar(i.ref)).find(Boolean);
    if (!item) continue;
    const p = precioDeEstandar(e);
    porTipo.set(e.tipo, { tipo: e.tipo, item, estandar: e, precio: p.valor ?? (Number(item.precioRef) || null), porConfirmar: p.porConfirmar, unidad: item.unidad || e.unidad || '' });
  }
  return [...porTipo.values()];
}

/** La linea que da el tipo de la seccion: la de cantidad con mas tipos (3 o mas); null si no hay o si la seccion no lleva. */
export function lineaDelTipo(cap, estandarDeLinea, itemDeRef = null) {
  if (!cap || SIN_PANTALLA_DE_TIPO.has(cap.numero)) return null;
  let mejor = null;
  for (const l of cap.lineas || []) {
    const est = estandarDeLinea ? estandarDeLinea(l) : null;
    if (claseDeLinea(l, est) !== 'cantidad') continue;
    const n = tarjetasDeLinea(l, est, itemDeRef).length;
    if (n >= MIN_TIPOS && (!mejor || n > mejor.n)) mejor = { l, n };
  }
  return mejor ? mejor.l : null;
}

/** Las pantallas del asistente en orden: { n, paso: 'tipo'|'cantidades'|'revision'|'resumen', titulo }. */
export function pantallasDelAsistente(recorrido, estandarDeLinea, itemDeRef = null) {
  const out = [];
  for (const cap of recorrido?.capitulos || []) {
    if (lineaDelTipo(cap, estandarDeLinea, itemDeRef)) out.push({ n: cap.numero, paso: 'tipo', titulo: 'Tipo' });
    out.push({ n: cap.numero, paso: 'cantidades', titulo: 'Cantidades' });
    if ((cap.lineas || []).some((l) => claseDeLinea(l, estandarDeLinea ? estandarDeLinea(l) : null) !== 'cantidad')) out.push({ n: cap.numero, paso: 'revision', titulo: 'Revisión' });
  }
  out.push({ n: null, paso: 'resumen', titulo: 'Resumen' });
  return out;
}

/**
 * La pantalla a la que lleva Atras (-1) o Siguiente (+1) desde idx, saltando las secciones que no aplican. Si no hay a
 * donde ir, se queda en idx.
 */
export function moverPantalla(pantallas, idx, dir, noAplica = new Set()) {
  let i = idx + dir;
  while (i >= 0 && i < pantallas.length && pantallas[i].n !== null && noAplica.has(pantallas[i].n)) i += dir;
  return i >= 0 && i < pantallas.length ? i : idx;
}

/** El texto del boton Siguiente: lo que sigue en la misma seccion, el nombre corto de la siguiente o el Resumen. */
export function textoSiguiente(pantallas, idx, noAplica, nombreCorto) {
  const j = moverPantalla(pantallas, idx, 1, noAplica);
  if (j === idx) return 'Guardar presupuesto';
  const p = pantallas[j];
  if (p.paso === 'resumen') return 'Siguiente: Resumen';
  return `Siguiente: ${p.n === pantallas[idx].n ? p.titulo : nombreCorto(p.n)}`;
}

/**
 * PR 3 (Cantidades): lo que se puede poner en una linea. Con item estandar, un estandar por opcion (su nombre armado y
 * el item de la plantilla buscado por ref); sin estandar, los items de la linea. Cada opcion: { etiqueta, item, precio,
 * unidad }. Sin item en la plantilla, la opcion no entra.
 */
export function opcionesDeLinea(l, est, itemDeRef = null) {
  if (est?.estandar?.length) {
    const buscar = itemDeRef || ((ref) => (l?.items || []).find((it) => String(it.fuenteId) === ref) || null);
    const out = [];
    for (const e of est.estandar) {
      const item = (e.items || []).map((i) => buscar(i.ref)).find(Boolean);
      if (item && !out.some((o) => o.item.id === item.id)) out.push({ etiqueta: e.nombre, item, precio: Number(item.precioRef) || 0, unidad: item.unidad || e.unidad || '', estandar: e });
    }
    if (out.length) return ordenarOpciones(out);
  }
  return (l?.items || []).map((it) => ({ etiqueta: String(it.descripcion || '').slice(0, 80), item: it, precio: Number(it.precioRef) || 0, unidad: it.unidad || '' }));
}

/** El primer numero colombiano ('1,30') en metros: la unidad del texto ('8 x 2,5 cm') la pasa de cm o mm. null sin numero. */
function enMetros(texto) {
  const t = String(texto || '');
  const m = t.match(/\d+(?:,\d+)?/);
  if (!m) return null;
  const n = Number(m[0].replace(',', '.'));
  const u = (t.match(/\d\s*(cm|mm|m)\b/i) || [])[1]?.toLowerCase();
  return u === 'cm' ? n / 100 : u === 'mm' ? n / 1000 : n;
}

/**
 * La medida de una opcion para ordenarla: [ancho, alto] de 'Ancho x alto' ('3,78 x (0,71 + 1,87)' suma el alto) o, si
 * no hay, la altura (las de cm pasan a metros). null si no tiene medida.
 */
export function medidaDeOpcion(o) {
  const car = o?.estandar?.caracteristicas || {};
  const valor = (re) => { const k = Object.keys(car).find((x) => re.test(x)); return k ? String((car[k] || [])[0] || '') : ''; };
  const ax = valor(/ancho\s*x\s*alto/i);
  if (ax) {
    const [a, b = ''] = ax.split(/\s*x\s*/i);
    const ancho = enMetros(a);
    const altos = (b.match(/\d+(?:,\d+)?/g) || []).map((n) => Number(n.replace(',', '.')));
    if (ancho !== null) return [ancho, altos.length ? altos.reduce((s, n) => s + n, 0) : 0];
  }
  // Solo la altura cuenta como medida de la opcion: la de la vareta o un diametro son del material (Deck), no del tamano.
  const n = enMetros(valor(/altura/i));
  return n === null ? null : [n, 0];
}

/**
 * MAD-CUAL-ORDEN-T1 (regla de Q #43838, Ares #43833): el orden de las tarjetas del Cual. La unidad que mas se repite
 * primero; luego ancho y alto de menor a mayor, y lo que no tiene medida al final por precio; las gemelas incluye/sin
 * juntas, la 'sin' primero.
 * No toca la data: solo el orden en pantalla. Estable: lo que empata queda como venia.
 */
export function ordenarOpciones(opciones) {
  const ops = [...(opciones || [])];
  const uni = (o) => String(o.unidad || '').trim().toLowerCase();
  const veces = new Map();
  ops.forEach((o) => veces.set(uni(o), (veces.get(uni(o)) || 0) + 1));
  const moda = [...veces.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
  const incluye = (o) => Object.entries(o.estandar?.caracteristicas || {}).filter(([k]) => /^incluye/i.test(k)).reduce((s, [, v]) => s + (v || []).length, 0);
  const clave = (o, i) => {
    const m = medidaDeOpcion(o);
    return [uni(o) === moda ? 0 : 1, m ? 0 : 1, m ? m[0] : 0, m ? m[1] : 0, Number(o.precio) || 0, i];
  };
  const ordenadas = ops.map((o, i) => ({ o, k: clave(o, i) }))
    .sort((a, b) => { for (let j = 0; j < a.k.length; j++) if (a.k[j] !== b.k[j]) return a.k[j] - b.k[j]; return 0; })
    .map((x) => x.o);
  // Las gemelas (iguales salvo lo que incluyen) quedan juntas donde aparece la primera, la que incluye menos primero.
  const gemela = (o) => String(o.estandar?.tipo || '') + '|' + JSON.stringify(Object.entries(o.estandar?.caracteristicas || {}).filter(([k]) => !/^incluye/i.test(k)).sort());
  const grupos = new Map();
  for (const o of ordenadas) { const g = gemela(o); if (!grupos.has(g)) grupos.set(g, []); grupos.get(g).push(o); }
  return [...grupos.values()].flatMap((g) => (g.length > 1 ? [...g].sort((a, b) => incluye(a) - incluye(b)) : g));
}

/**
 * Como esta la linea en el presupuesto: puestos = ids de plantilla de lo que ya lleva y su cantidad. Devuelve
 * { puesta, op, cantidad }: la opcion puesta (o la elegida antes, o la primera) y su cantidad.
 */
export function filaEnPresupuesto(opciones, puestos, elegida = 0) {
  const k = opciones.findIndex((o) => puestos.has(o.item.id));
  if (k >= 0) return { puesta: true, op: k, cantidad: puestos.get(opciones[k].item.id) };
  return { puesta: false, op: Math.min(Math.max(0, elegida || 0), Math.max(0, opciones.length - 1)), cantidad: null };
}

/** El Resumen: por seccion lo que suma y cuantas revisiones quedan pendientes (sin respuesta o 'pendiente'). */
export function resumenPorSeccion(recorrido, valorDeSeccion, chequeoDe, estandarDeLinea = null) {
  return (recorrido?.capitulos || []).map((c) => {
    const revisar = (c.lineas || []).filter((l) => claseDeLinea(l, estandarDeLinea ? estandarDeLinea(l) : null) === 'chequeo');
    const pendientes = revisar.filter((l) => { const v = chequeoDe(c.numero, l.linea); return v !== 'confirmado' && v !== 'no_aplica'; }).length;
    return { numero: c.numero, nombre: c.nombre, valor: Number(valorDeSeccion(c.numero)) || 0, pendientes };
  });
}

/**
 * MAD-CUAL-CORTO-T1 (maqueta aprobada, Ares #43695): la etiqueta corta del "Cual" de cada opcion de una linea. Solo lo
 * que distingue a sus opciones: las caracteristicas (y el tipo) con valor en mas opciones y mas valores distintos, una
 * a una hasta que las etiquetas no se repitan (tope 3), sin lo que incluye y cada valor una vez ("Doble drywall" ya
 * dice drywall). Dos opciones que quedan iguales se separan por lo que una incluye y la otra no.
 * opciones = las de opcionesDeLinea (con su estandar); plantilla = la de la linea. Devuelve { distingue, cortas }.
 */
export function etiquetasCortas(opciones, plantilla) {
  const ops = opciones || [];
  if (!ops.some((o) => o.estandar)) return { distingue: [], cortas: ops.map((o) => ({ corto: String(o.etiqueta || ''), extra: '' })) };
  const cars = ((plantilla && plantilla.caracteristicas) || []).filter((c) => !c.obra);
  const esIncluye = (k) => /^incluye/i.test(String(k));
  const unidad = (k) => { const c = cars.find((x) => x.nombre === k); return c && c.control === 'numero' && c.unidad ? ' ' + c.unidad : ''; };
  const val = (o, k) => (k === 'Tipo' ? String(o.estandar?.tipo || '').trim() : ((o.estandar?.caracteristicas || {})[k] || []).join(' y '));
  const etiqueta = (o, ks) => ks.reduce((partes, k) => {
    const v = val(o, k);
    const dicho = partes.join(' ').toLowerCase();
    const palabras = v.toLowerCase().split(/\s+/).filter((p) => p.length > 3);
    if (!v || (palabras.length && palabras.every((p) => dicho.includes(p)))) return partes;
    return [...partes, v + unidad(k)];
  }, []).join(' · ');
  const candidatos = ['Tipo', ...cars.map((c) => c.nombre)].filter((k) => !esIncluye(k) && new Set(ops.map((o) => val(o, k))).size > 1);
  const puntaje = (k) => ops.filter((o) => val(o, k)).length * new Set(ops.map((o) => val(o, k)).filter(Boolean)).size;
  const orden = [...candidatos].sort((a, b) => puntaje(b) - puntaje(a));
  const distingue = [];
  for (const k of orden) {
    const yaDicho = ops.every((o) => !val(o, k) || etiqueta(o, distingue).toLowerCase().includes(val(o, k).toLowerCase()));
    if (distingue.length && yaDicho) continue;
    distingue.push(k);
    const ets = ops.map((o) => etiqueta(o, distingue));
    if (new Set(ets).size === ets.length || distingue.length >= 3) break;
  }
  // Sin valor en lo que distingue, la tarjeta dice su nombre (corto): "Sin detalle" no deja elegir.
  const nombre = (o) => { const t = String(o.etiqueta || o.estandar?.tipo || '').trim(); return t.length > 60 ? t.slice(0, 58) + '…' : t; };
  const cortas = ops.map((o) => ({ corto: etiqueta(o, distingue) || nombre(o) || 'Sin detalle', extra: '' }));
  const incluye = (o) => Object.entries(o.estandar?.caracteristicas || {}).filter(([k]) => esIncluye(k)).flatMap(([, v]) => v);
  ops.forEach((o, i) => {
    const gemelos = ops.filter((x, j) => j !== i && cortas[j].corto === cortas[i].corto);
    if (!gemelos.length) return;
    const deOtros = new Set(gemelos.flatMap(incluye));
    const propio = incluye(o).filter((v) => !deOtros.has(v));
    const falta = [...deOtros].filter((v) => !incluye(o).includes(v));
    cortas[i].extra = propio.length ? 'incluye ' + propio.join(', ').toLowerCase() : (falta.length ? 'sin ' + falta.join(', ').toLowerCase() : '');
  });
  return { distingue, cortas };
}
